import i18n, { type AppLanguageCode, type TKey, translateApp } from '@repo/i18n'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

/** Backend error bodies are thrown directly by the SDK; `code` is top-level. */
export function getErrorCode(error: unknown) {
	return typeof error === 'object' &&
		error !== null &&
		'code' in error &&
		typeof error.code === 'string'
		? error.code
		: undefined
}

export function formatError(error: unknown, language?: AppLanguageCode) {
	const code = getErrorCode(error)
	// A 429 body carries `retryAfterSeconds`: its text states the wait as
	// whole minutes, rounded up, in the plural `count`. `exists` needs the
	// same options to find a key written only in plural forms.
	const options =
		typeof error === 'object' &&
		error !== null &&
		'retryAfterSeconds' in error &&
		typeof error.retryAfterSeconds === 'number'
			? { count: Math.ceil(error.retryAfterSeconds / 60) }
			: undefined
	const key = `shared:http_errors.${code?.toLowerCase()}` as TKey
	const resolvedKey =
		code && i18n.exists(key, options)
			? key
			: ('shared:http_errors.default' as TKey)

	return language
		? translateApp(language, resolvedKey, options)
		: i18n.t(resolvedKey, options)
}

export function debounce<Args extends unknown[]>(
	callback: (...args: Args) => unknown,
	delay: number
) {
	let timeout: Parameters<typeof clearTimeout>[0] | undefined

	return (...args: Args) => {
		if (timeout !== undefined) {
			clearTimeout(timeout)
		}
		timeout = setTimeout(() => {
			callback(...args)
		}, delay)
	}
}

export function getInitial(value: string) {
	return value.trim().charAt(0).toUpperCase() || '?'
}

export function getInitials(value: string) {
	return value
		.split(' ')
		.map((part) => part[0] ?? '')
		.join('')
		.slice(0, 2)
		.toUpperCase()
}
